import React, { useContext, useEffect, useState } from 'react';
import * as _ from 'lodash';
import { Link, useRouteMatch } from 'react-router-dom';

import config from '../../../config';
import paths from '../../../constants/routes.json';
import { ListingDetails_ListingData_listing } from '../../../types/graph/ListingDetails_ListingData';
import getModalContext from '../../../context/getModalContext';
import { EyeIcon } from '@heroicons/react/outline';

interface IProps {
  listing: ListingDetails_ListingData_listing;
  onListingDelete: (listingId: string) => void;
}

const Header: React.FC<IProps> = ({ listing, onListingDelete }) => {
  const modals = useContext(getModalContext());

  return (
    <div className="rounded-lg bg-white overflow-hidden shadow">
      <h2 className="sr-only" id="profile-overview-title">
        Listing Details
      </h2>
      <div className="bg-white p-6">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="sm:flex sm:space-x-5">
            <div className="flex-shrink-0">
              {_.get(
                listing.property?.media?.filter(
                  (i) => !!i && i.type === 'image'
                ),
                '[0].filePath',
                false
              ) ? (
                <img
                  className="mx-auto h-20 w-20 rounded-full"
                  src={_.get(
                    listing.property?.media?.filter(
                      (i) => !!i && i.type === 'image'
                    ),
                    '[0].filePath',
                    ''
                  )}
                  alt="tmp"
                />
              ) : (
                <svg
                  className="mx-auto h-20 w-20 rounded-full text-gray-300"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="mt-4 text-center sm:mt-0 sm:pt-1 sm:text-left">
              <p className="text-sm font-medium text-gray-600">Listing</p>
              <div className="flex items-start">
                <p className="text-xl font-bold text-gray-900 sm:text-2xl">
                  {_.get(listing, 'property.address.street', '')}
                  <p className="text-gray-400 text-sm font-normal">
                    {`${_.get(listing, 'property.address.city', '')}, ${_.get(
                      listing,
                      'property.address.region',
                      ''
                    )}, ${_.get(listing, 'property.address.country', '')}`}
                  </p>
                </p>
                <span
                  className={`${
                    _.get(listing, 'status', null) === 'active'
                      ? 'text-green-800 bg-green-100'
                      : 'text-gray-800 bg-gray-200'
                  } ml-8 mt-2 px-2 py-1 text-xs font-medium rounded-full`}
                >
                  {(_.get(listing, 'status', 'disabled') || 'disabled')
                    .charAt(0)
                    .toUpperCase() +
                    (_.get(listing, 'status', 'disabled') || 'disabled')
                      .toLowerCase()
                      .slice(1)}
                </span>
              </div>
            </div>
          </div>
          <div className="mt-5 space-x-4 flex justify-center sm:mt-0">
            <button
              disabled={listing.status !== 'active'}
              className={`${
                listing.status !== 'active' && 'cursor-not-allowed'
              } inline-flex items-center border px-4 py-2 shadow-sm text-sm font-medium rounded-md border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none`}
              onClick={() =>
                window.open(`${config.sites.host}/${listing.sid}`, '_blank')
              }
            >
              <EyeIcon className="w-5 h-5 text-gray-600" />
              <span className="ml-3">View</span>
            </button>
            <Link
              to={`${paths.LISTINGS}/${listing!.id}/sharing`}
              type="button"
              className={`${
                useRouteMatch(paths.LISTING_SHARING)
                  ? 'text-primary-dark ring-primary-light ring-offset-2 ring-2'
                  : ''
              } inline-flex items-center border px-4 py-2 shadow-sm text-sm font-medium rounded-md border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none`}
            >
              {/* Heroicon name: solid/share */}
              <svg
                className="-mx-1 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
              </svg>
            </Link>
            <Link
              to={`${paths.LISTINGS}/${listing!.id}/advanced`}
              type="button"
              className={`${
                useRouteMatch(paths.LISTING_ADVANCED)
                  ? 'text-primary-dark ring-primary-light ring-offset-2 ring-2'
                  : ''
              } inline-flex items-center border px-4 py-2 shadow-sm text-sm font-medium rounded-md border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none`}
            >
              {/* Heroicon name: solid/gear */}
              <svg
                className="-mx-1 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 bg-gray-50 grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-5 sm:divide-y-0 sm:divide-x">
        <div className="text-sm font-medium text-center">
          {/* Current: "border-indigo-500 text-indigo-600", Default: "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300" */}
          <Link
            to={`${paths.LISTINGS}/${listing!.id}/property/details`}
            className={`${
              useRouteMatch(paths.LISTING_PROPERTY)
                ? 'border-primary text-primary-dark'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } px-6 py-4 group inline-flex items-center border-b-2 font-medium text-sm`}
            aria-current="page"
          >
            {/* Current: "text-indigo-500", Default: "text-gray-400 group-hover:text-gray-500" */}
            {/* Heroicon name: solid/house */}
            <svg
              className={`${
                useRouteMatch(paths.LISTING_PROPERTY)
                  ? 'text-primary'
                  : 'text-gray-400 group-hover:text-gray-500'
              } -ml-0.5 mr-2 h-5 w-5`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
            <span>Property</span>
          </Link>
        </div>
        <div className="text-sm font-medium text-center">
          <Link
            to={`${paths.LISTINGS}/${listing!.id}/contacts`}
            className={`${
              useRouteMatch(paths.LISTING_CONTACTS)
                ? 'border-primary text-primary-dark'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } px-6 py-4 group inline-flex items-center border-b-2 font-medium text-sm`}
          >
            {/* Heroicon name: solid/office-building */}
            <svg
              className={`${
                useRouteMatch(paths.LISTING_CONTACTS)
                  ? 'text-primary'
                  : 'text-gray-400 group-hover:text-gray-500'
              } -ml-0.5 mr-2 h-5 w-5`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            <span>Contacts</span>
          </Link>
        </div>
        <div className="text-sm font-medium text-center">
          <Link
            to={`${paths.LISTINGS}/${listing!.id}/website`}
            className={`${
              useRouteMatch(paths.LISTING_WEBSITE)
                ? 'border-primary text-primary-dark'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } px-6 py-4 group inline-flex items-center border-b-2 font-medium text-sm`}
          >
            {/* Heroicon name: solid/office-building */}
            <svg
              className={`${
                useRouteMatch(paths.LISTING_WEBSITE)
                  ? 'text-primary'
                  : 'text-gray-400 group-hover:text-gray-500'
              } -ml-0.5 mr-2 h-5 w-5`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                clipRule="evenodd"
              />
            </svg>
            <span>Website</span>
          </Link>
        </div>
        <div className="text-sm font-medium text-center">
          <Link
            to={`${paths.LISTINGS}/${listing!.id}/marketing`}
            className={`${
              useRouteMatch(paths.LISTING_MARKETING)
                ? 'border-primary text-primary-dark'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } px-6 py-4 group inline-flex items-center border-b-2 font-medium text-sm`}
          >
            {/* Heroicon name: solid/office-building */}
            <svg
              className={`${
                useRouteMatch(paths.LISTING_MARKETING)
                  ? 'text-primary'
                  : 'text-gray-400 group-hover:text-gray-500'
              } -ml-0.5 mr-2 h-5 w-5`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span>Marketing</span>
          </Link>
        </div>
        {/* <div className="text-sm font-medium text-center">
          <Link
            to={`${paths.LISTINGS}/${listing!.id}/streams`}
            className={`${
              useRouteMatch(paths.LISTING_STREAMS)
                ? 'border-primary text-primary-dark'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } px-6 py-4 group inline-flex items-center border-b-2 font-medium text-sm`}
          >
            
            <svg
              className={`${
                useRouteMatch(paths.LISTING_STREAMS)
                  ? 'text-primary'
                  : 'text-gray-400 group-hover:text-gray-500'
              } -ml-0.5 mr-2 h-5 w-5`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
            </svg>
            <span>Streaming</span>
          </Link>
        </div> */}
        <div className="text-sm font-medium text-center">
          <Link
            to={`${paths.LISTINGS}/${listing!.id}/analytics`}
            className={`${
              useRouteMatch(paths.LISTING_ANALYTICS)
                ? 'border-primary text-primary-dark'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } px-6 py-4 group inline-flex items-center border-b-2 font-medium text-sm`}
          >
            {/* Heroicon name: solid/office-building */}
            <svg
              className={`${
                useRouteMatch(paths.LISTING_ANALYTICS)
                  ? 'text-primary'
                  : 'text-gray-400 group-hover:text-gray-500'
              } -ml-0.5 mr-2 h-5 w-5`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
            </svg>
            <span>Analytics</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Header;
